import { colors, Icons } from 'bold-ui'
import { CboSelectModel, SimNaoEnum } from 'components/form'
import { CidSelectFieldModel } from 'components/form/field/select/CidSelectField/CidSelectField'
import { DoseImunobiologicoVacinacaoSelectFieldModel } from 'components/form/field/select/DoseImunobiologicoVacinacaoSelectField'
import { EstrategiaVacinacaoSelectFieldModel } from 'components/form/field/select/EstrategiaVacinacaoSelectField'
import { GrupoAtendimentoSelectFieldModel } from 'components/form/field/select/GrupoAtendimentoSelectField'
import { LocalAplicacaoSelectFieldModel } from 'components/form/field/select/LocalAplicacaoSelectField'
import { LoteImunobiologicoSelectModel } from 'components/form/field/select/LoteImunobiologicoSelectField/LoteImunobiologicoSelectField'
import { ViaAdministracaoVacinaSelectModel } from 'components/form/field/select/ViaAdministracaoVacinaSelectField'
import theme from 'config/theme'
import {
  CondicaoMaternalDbEnum,
  EsquemaCalendarioVacinalQuery,
  EstrategiaVacinacaoEnum,
  OrigemDadoEnum,
  TipoAtendimentoProfissional,
  TipoRegistroVacinacaoEnum,
} from 'graphql/types.generated'
import { tipoAtendimentoToTipoServicoMap } from 'types/enums'
import { metaPath } from 'util/metaPath'
import { ImunobiologicoSelectModel } from 'view/lote-imunobiologico/components/ImunobiologicoSelectField'
import type { LoteImunobiologicoFabricanteSelectFieldModel } from 'view/lote-imunobiologico/components/LoteImunobiologicoFabricanteSelectField'

import type { DoseItemProps } from '../detail/components/vacinacao/DoseItem'
import { FinalizacaoAtendimentoFormModel } from '../detail/soap/finalizacao'
import { ManterCidadaoLista } from '../detail/soap/finalizacao/components/DesfechoAtendimentoPanel'
import { TiposServicoEscutaInicial } from '../escuta-inicial/model-escutaInicial'
import { RetificacaoAtendimentoState } from '../model-atendimento'
import { AgendamentoDia } from '../types/AgendamentoDia'
import { getCondicaoGestantePuerpera } from './utils-vacinacao'
import { imunosComValidacoesIds, ValidacoesVacinacaoEnum } from './validator-atendimentoVacinacao'

export interface AtendimentoVacinacaoState {
  condicoesVacinacao: CondicoesFormModel
  finalizacaoVacinacao: FinalizacaoAtendimentoFormModel
  registroVacinacao: ReadonlyArray<RegistroVacinacaoFormModel>
  imprimirAtendimento?: boolean
  lastSaved?: Date
  retificacaoAtendimento?: RetificacaoAtendimentoState
}

export const initialValue = (
  puerpera: boolean,
  gestante: boolean,
  tiposServico: ReadonlyArray<TiposServicoEscutaInicial>,
  canCreateNovoAtendimento: boolean,
  agendamentosDia: ReadonlyArray<AgendamentoDia>
): AtendimentoVacinacaoState => {
  const tiposServicoAtendimento = tiposServico
    ?.map((t) => t.id)
    .filter((item) => !tipoAtendimentoToTipoServicoMap[TipoAtendimentoProfissional.VACINACAO].includes(item))

  const manterNaLista = canCreateNovoAtendimento && (agendamentosDia?.length || tiposServicoAtendimento?.length > 0)

  return {
    condicoesVacinacao: {
      condicao: getCondicaoGestantePuerpera(gestante, puerpera),
      comunicanteHanseniase: false,
      viajante: false,
    },
    lastSaved: new Date(),
    registroVacinacao: [],
    finalizacaoVacinacao: {
      desfechoAtendimento: {
        atendimento: { tiposServico: tiposServicoAtendimento },
        manterCidadaoLista: manterNaLista ? ManterCidadaoLista.SIM : ManterCidadaoLista.NAO,
      },
    },
  } as AtendimentoVacinacaoState
}

export interface CondicoesFormModel {
  condicao: CondicaoMaternalDbEnum
  viajante: boolean
  comunicanteHanseniase: boolean
}

export interface VacinacaoRegistrosLink {
  imunobiologicoId: ID
  doseId: ID
  idUnicoRegistro: ID
  estrategiaVacinacao?: EstrategiaVacinacaoSelectFieldModel
  dataAprazamentoProxDose?: LocalDate
}

export interface RegistroVacinacaoFormModel {
  id: ID
  idUnicoRegistro: ID
  tipoRegistroVacinacao?: TipoRegistroVacinacaoEnum
  isRegistroAnterior?: boolean
  outrosImunosImunobiologico?: ImunobiologicoSelectModel
  imunobiologicoId?: string
  imunobiologicoNome?: string
  imunobiologicoSigla?: string
  estrategiaVacinacao?: EstrategiaVacinacaoSelectFieldModel
  grupoAtendimento?: GrupoAtendimentoSelectFieldModel
  outrosImunosDose?: DoseImunobiologicoVacinacaoSelectFieldModel
  doseId?: string
  doseNome?: string
  doseSigla?: string
  dataAprazamento?: LocalDate
  // Utilizado quando o registro possui uma data de aprazamento automatico para linkar com o registro de aprazamento criado
  dataAprazamentoProxDose?: LocalDate
  // Utilizado para linkar o aprazamento automatico com a aplicacao que o criou
  aprazamentoProxDoseLink?: VacinacaoRegistrosLink
  // Utilizado para linkar a aplicacao com o aprazamento automatico criado
  aplicacaoLink?: VacinacaoRegistrosLink
  dataAplicacao?: LocalDate
  dataRegistro: LocalDate
  loteNome?: string
  loteFabricante?: LoteImunobiologicoFabricanteSelectFieldModel
  loteDataValidade?: LocalDate
  loteImunobiologico?: LoteImunobiologicoSelectModel
  viaAdministracao?: ViaAdministracaoVacinaSelectModel
  localAplicacaoVacinacao?: LocalAplicacaoSelectFieldModel
  observacoes?: string
  origemDados: OrigemDadoEnum
  condicaoMaternal: CondicaoMaternalDbEnum
  isViajante: boolean
  isComunicanteHanseniase?: SimNaoEnum
  isCadastrarNovoLote?: boolean
  proximaDoseSigla?: string
  proximaDoseId?: ID
  proximaDoseNome?: string
  isPesquisaEstrategia?: boolean
  isAplicadoExterior?: boolean
  cboPrescritor: CboSelectModel
  cid10MotivoIndicacao: CidSelectFieldModel
  isPesquisaClinica: SimNaoEnum
  anvisaProtocoloEstudo: string
  anvisaProtocoloVersao: string
  anvisaNumeroRegistro: string
}

export const meta = metaPath<AtendimentoVacinacaoState>()

export interface ImunobiologicoRowModel {
  imunobiologicoId: ID
  nome: string
  doses: DoseItemProps[]
  ordem: number
}

export interface DoseItemModel {
  calendarioId: ID
  imunobiologicoId: ID
  doseId: ID
  status: StatusDoseEnum
  nome: string
  idadeRecomendada: string
  dataAprazamento?: string
  registradoAtendimento?: boolean
  mostrarNoCalendarioDoGrupo: boolean
  imunobiologicoNome: string
  imunobiologicoNomeCompleto: string
  imunobiologicoSigla: string
  prontuarioId: ID
  cidadaoCpf: string
  cidadaoId: string
  dataAplicacao?: string
  quantidadeAplicacoes?: number
  dataRegistroAprazamento?: Instant
  ordem?: number
  hasDoseEquivalenteOnly: boolean
  doseSigla: string
}

export const isBcg = (idImunobiologico: string) =>
  idImunobiologico === imunosComValidacoesIds[ValidacoesVacinacaoEnum.BCG][0]

export const isDtpaAdulto = (idImunobiologico: string) =>
  idImunobiologico === imunosComValidacoesIds[ValidacoesVacinacaoEnum.DTPA_ADULTO][0]

export const isEstrategiaEspecial = (estrategiaVacinacao: any): boolean =>
  EstrategiaVacinacaoEnum.ESPECIAL === estrategiaVacinacao?.estrategiaVacinacaoDbEnum

export enum StatusDoseEnum {
  APLICADA,
  DISPONIVEL,
  ATRASADA,
  FUTURA,
  EQUIVALENTE,
}

interface StatusProps {
  hintVacinacao: string
  hintAcompanhamento: string
  hintProntuario: string
  icon?: Icons
  fontColor: string
  tagColor?: string
  tagTextColor?: string
  backgroundColor: string
  borderColor: string
}

export const statusDoseRecord: Record<StatusDoseEnum, StatusProps> = {
  [StatusDoseEnum.APLICADA]: {
    hintVacinacao: 'Aplicada - Visualizar ou realizar registro',
    hintAcompanhamento: 'Visualizar registro',
    hintProntuario: 'Visualizar registro',
    icon: 'checkCircleFilled',
    fontColor: theme.pallete.status.success.c40,
    tagColor: theme.pallete.status.success.c40,
    tagTextColor: theme.pallete.gray.c100,
    backgroundColor: theme.pallete.status.success.c90,
    borderColor: theme.pallete.status.success.c60,
  },
  [StatusDoseEnum.DISPONIVEL]: {
    hintVacinacao: 'A aplicar - Visualizar ou realizar registro',
    hintAcompanhamento: 'Adicionar transcrição de caderneta',
    hintProntuario: 'Não há nenhum registro',
    fontColor: theme.pallete.gray.c10,
    backgroundColor: theme.pallete.gray.c100,
    borderColor: theme.pallete.gray.c60,
  },
  [StatusDoseEnum.ATRASADA]: {
    hintVacinacao: 'Atrasada - Visualizar ou realizar registro',
    hintAcompanhamento: 'Adicionar transcrição de caderneta',
    hintProntuario: 'Não há nenhum registro',
    icon: 'timesFilled',
    fontColor: theme.pallete.status.danger.c40,
    tagColor: theme.pallete.status.danger.c40,
    tagTextColor: theme.pallete.gray.c100,
    backgroundColor: theme.pallete.status.danger.c90,
    borderColor: theme.pallete.status.danger.c60,
  },
  [StatusDoseEnum.FUTURA]: {
    hintVacinacao: 'A aprazar - Visualizar ou realizar registro',
    hintAcompanhamento: 'Adicionar transcrição de caderneta',
    hintProntuario: 'Não há nenhum registro',
    icon: 'clockFilled',
    fontColor: theme.pallete.gray.c10,
    tagColor: theme.pallete.gray.c50,
    tagTextColor: theme.pallete.gray.c100,
    backgroundColor: theme.pallete.gray.c80,
    borderColor: theme.pallete.gray.c60,
  },
  [StatusDoseEnum.EQUIVALENTE]: {
    hintVacinacao: 'Dose equivalente aplicada',
    hintAcompanhamento: 'Dose equivalente aplicada',
    hintProntuario: 'Dose equivalente aplicada',
    fontColor: colors.turquoise.c40,
    backgroundColor: colors.turquoise.c90,
    borderColor: colors.turquoise.c40,
    tagColor: colors.turquoise.c40,
    tagTextColor: theme.pallete.gray.c100,
  },
}

interface OrigemDadoProps {
  label: string
  hint: string
}

export const origemDadosVacinacao: Partial<Record<OrigemDadoEnum, OrigemDadoProps>> = {
  [OrigemDadoEnum.PEC]: {
    label: 'PEC',
    hint: 'Registro realizado através do PEC desta instalação',
  },
  [OrigemDadoEnum.CDS]: {
    label: 'CDS',
    hint: 'Registro realizado através do CDS',
  },
  [OrigemDadoEnum.SISTEMA_TERCEIRO]: {
    label: 'Sistema 3º',
    hint: 'Registro realizado através de sistema 3º',
  },
  [OrigemDadoEnum.RAS_PEC]: {
    label: 'RAS PEC',
    hint: 'Registro realizado através do PEC de outra instalação',
  },
  [OrigemDadoEnum.APP_VACINACAO]: {
    label: 'e-SUS Vacinação',
    hint: 'Registro realizado através do aplicativo e-SUS Vacinação.',
  },
}

export interface ImunobiologicoModel {
  id: ID
  sigla: string
  nome: string
}

export interface InfoCidadaoVacina {
  cidadaoId: ID
  prontuarioId: ID
  cidadaoCpf: string
}

export type BaseCalendarioVacinalModel = EsquemaCalendarioVacinalQuery['esquemaCalendarioVacinal'][0]

export const ESTRATEGIAS_GRUPO_ATENDIMENTO_REQUIRED = [
  EstrategiaVacinacaoEnum.CAMPANHA_INDISCRIMINADA,
  EstrategiaVacinacaoEnum.CAMPANHA_SELETIVA,
]

export interface VacinaEquivalenteModel {
  imunobiologicoId: ID
  imunobiologicoEquivalenteId: ID
  dosesEquivalentesIds: ID[] | null
  estrategiaId: ID | null
  doseAtualId: ID
}

export enum VacinacaoDoseIdEnum {
  PRIMEIRA_DOSE = '1',
  SEGUNDA_DOSE = '2',
  DOSE_UNICA = '9',
  DOSE = '8',
  REFORCO = '38',
  PRIMEIRO_REFORCO = '6',
  SEGUNDO_REFORCO = '7',
}

export enum VacinacaoImunobiologicoIdEnum {
  HPV_QUADRI = '67',
  FEBRE_AMARELA = '14',
  MENINGO_C = '41',
  MENINGO_ACWY = '74',
  HEPATITE_A_INF = '55',
  VIP = '22',
  VOP = '28',
  PNEUMO_10 = '26',
  PNEUMO_20 = '107',
}

export enum VacinacaoEstrategiaIdEnum {
  ROTINA_ID = '1',
}

// doseEquivalenteId: null quer dizer que qualquer dose aplicada pode ser considerada como equivalente
export const dosesEquivalentesItems: VacinaEquivalenteModel[] = [
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.HEPATITE_A_INF,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.HEPATITE_A_INF,
    dosesEquivalentesIds: null,
    estrategiaId: VacinacaoEstrategiaIdEnum.ROTINA_ID,
    doseAtualId: VacinacaoDoseIdEnum.DOSE_UNICA,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.FEBRE_AMARELA,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.FEBRE_AMARELA,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRA_DOSE],
    estrategiaId: VacinacaoEstrategiaIdEnum.ROTINA_ID,
    doseAtualId: VacinacaoDoseIdEnum.DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.MENINGO_C,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.MENINGO_C,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRO_REFORCO],
    estrategiaId: VacinacaoEstrategiaIdEnum.ROTINA_ID,
    doseAtualId: VacinacaoDoseIdEnum.REFORCO,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.MENINGO_C,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.MENINGO_ACWY,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.PRIMEIRA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.MENINGO_C,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.MENINGO_ACWY,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.SEGUNDA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.SEGUNDA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.HPV_QUADRI,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.HPV_QUADRI,
    dosesEquivalentesIds: null,
    estrategiaId: VacinacaoEstrategiaIdEnum.ROTINA_ID,
    doseAtualId: VacinacaoDoseIdEnum.DOSE_UNICA,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.VIP,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.VOP,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRO_REFORCO],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.PRIMEIRO_REFORCO,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.VIP,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.VIP,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.REFORCO],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.PRIMEIRO_REFORCO,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.VIP,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.VOP,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.SEGUNDO_REFORCO],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.SEGUNDO_REFORCO,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.PRIMEIRA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.SEGUNDA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.SEGUNDA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.DOSE_UNICA],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.DOSE_UNICA,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.REFORCO],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.REFORCO,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.PRIMEIRA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.PRIMEIRA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.SEGUNDA_DOSE],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.SEGUNDA_DOSE,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.DOSE_UNICA],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.DOSE_UNICA,
  },
  {
    imunobiologicoId: VacinacaoImunobiologicoIdEnum.PNEUMO_20,
    imunobiologicoEquivalenteId: VacinacaoImunobiologicoIdEnum.PNEUMO_10,
    dosesEquivalentesIds: [VacinacaoDoseIdEnum.REFORCO],
    estrategiaId: null,
    doseAtualId: VacinacaoDoseIdEnum.REFORCO,
  },
]

export const getVacinaEquivalenciasByDoseAtual = (
  imunobiologicoId: ID,
  doseId: ID,
  estrategiaId: ID
): VacinaEquivalenteModel[] => {
  return dosesEquivalentesItems.filter(
    (item) =>
      item.imunobiologicoId === imunobiologicoId &&
      item.doseAtualId === doseId &&
      (item.estrategiaId === estrategiaId || item.estrategiaId === null || estrategiaId === null)
  )
}
